import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Plus,
  Upload,
  X,
  Download,
  FileText,
  Image,
  Tag,
  Eye,
  Lock,
  Trash2,
  Database,
} from "lucide-react";
import {
  useCreateDatasetMutation,
  useUpdateDatasetMutation,
} from "../api/createDatasetApi";
import TiptapEditor from "../components/ui/TiptapEditor";
import { useDropzone } from "react-dropzone";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import Frame from "../assets/Frame.png";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "@/components/ui/button";

interface AddNewDatasetFormData {
  title: string;
  description: string;
  files: File[];
  tags: string[];
  coverPhoto: File | null;
  visibility: "PRIVATE" | "PUBLIC" | "PROJECT";
}

interface AddNewDatasetPageProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOption?: string;
  onOptionChange?: (option: string) => void;
  onContinue?: () => void;
  onSuccess?: (dataset?: any) => void;
  dataset?: any;
}

const AddNewDatasetPage: React.FC<AddNewDatasetPageProps> = ({
  isOpen,
  onClose,
  selectedOption = "newProject",
  onOptionChange = () => {},
  onContinue = () => {},
  onSuccess = () => {},
  dataset,
}) => {
  // reference optional props to avoid unused variable TS errors
  const {
    selectedOption: _selectedOption = "newProject",
    onOptionChange: _onOptionChange = () => {},
    onContinue: _onContinue = () => {},
    onSuccess: _onSuccess = () => {},
  } = { selectedOption, onOptionChange, onContinue, onSuccess } as any;
  void _selectedOption;
  void _onOptionChange;
  void _onContinue;
  void _onSuccess;
  const [formData, setFormData] = useState<AddNewDatasetFormData>({
    title: "",
    description: "",
    files: [],
    tags: [],
    coverPhoto: null,
    visibility: "PRIVATE",
  });
  const [tagInput, setTagInput] = useState("");
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const [existingCoverPhotoUrl, setExistingCoverPhotoUrl] = useState<
    string | null
  >(null);
  const [removeExistingCoverPhoto, setRemoveExistingCoverPhoto] =
    useState(false);
  const [removedFiles, setRemovedFiles] = useState<any[]>([]);

  const isEdit = !!dataset;

  useEffect(() => {
    if (dataset) {
      setFormData({
        title: dataset.title || "",
        description: dataset.description || "",
        files: [], // Files can't be pre-populated for editing
        tags: dataset.tags || [],
        coverPhoto: null, // Cover photo URL can be displayed separately
        visibility: dataset.visibility || "PRIVATE",
      });
      setExistingFiles(dataset.files || []);
      setExistingCoverPhotoUrl(dataset.cover_photo_url || null);
      setRemoveExistingCoverPhoto(false);
    } else {
      setFormData({
        title: "",
        description: "",
        files: [],
        tags: [],
        coverPhoto: null,
        visibility: "PRIVATE",
      });
      setExistingFiles([]);
    }
  }, [dataset]);

  const coverPhotoInputRef = useRef<HTMLInputElement>(null);

  const createDatasetMutation = useCreateDatasetMutation();
  const updateDatasetMutation = useUpdateDatasetMutation();

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, title: e.target.value });
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !formData.tags.includes(newTag)) {
        setFormData({
          ...formData,
          tags: [...formData.tags, newTag],
        });
        setTagInput("");
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validExtensions = ["xls", "xlsx", "csv", "json", "parquet", "pdf", "txt", "text"];
    const newFiles = acceptedFiles.filter((file) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      return validExtensions.includes(fileExtension || "");
    });

    if (newFiles.length > 0) {
      setFormData((prev) => ({ ...prev, files: [...prev.files, ...newFiles] }));
    } else {
      alert("Please upload valid files (XLS, XLSX, CSV, JSON, PARQUET, PDF, TXT)");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingFile = (index: number) => {
    const fileToRemove = existingFiles[index];
    setExistingFiles((prev) => prev.filter((_, i) => i !== index));
    setRemovedFiles((prev) => [...prev, fileToRemove]);
  };

  const handleCoverPhotoSelect = (files: FileList | null) => {
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        setFormData({ ...formData, coverPhoto: file });
      } else {
        alert("Please upload a valid image file");
      }
    }
  };

  const handleVisibilityChange = (value: "PRIVATE" | "PROJECT" | "PUBLIC") => {
    setFormData({ ...formData, visibility: value });
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      description: "",
      files: [],
      tags: [],
      coverPhoto: null,
      visibility: "PRIVATE",
    });
    setTagInput("");
    onClose();
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) {
      alert("Please enter a dataset title");
      return;
    }

    // File validation
    if (isEdit) {
      if (formData.files.length === 0 && existingFiles.length === 0) {
        alert("Please upload at least one file");
        return;
      }
    } else {
      if (formData.files.length === 0) {
        alert("Please upload a file");
        return;
      }
    }

    // Prevent multiple submissions
    if (createDatasetMutation.isPending || updateDatasetMutation.isPending) {
      return;
    }

    try {
      if (isEdit && dataset) {
        // Update dataset
        const payload = {
          title: formData.title,
          description: formData.description,
          visibility: formData.visibility,
          tags: formData.tags,
          filesToDelete: removedFiles.map(
            (file) => file.key || file.file_details?.file_name,
          ),
          datasetFile: formData.files,
          coverPhoto: formData.coverPhoto,
          removeCoverPhoto: removeExistingCoverPhoto && !formData.coverPhoto,
        };

        const response = await updateDatasetMutation.mutateAsync({
          id: dataset.id,
          payload,
        });

        // Pass the updated dataset back to onSuccess callback
        const updatedDataset = response?.data;
        onSuccess(updatedDataset);
        onClose();
      } else {
        // Create dataset
        const fd = new FormData();
        fd.append("title", formData.title);
        fd.append("description", formData.description);
        fd.append("visibility", formData.visibility);
        // append tags as JSON array
        fd.append("tags", JSON.stringify(formData.tags));
        // main files
        formData.files.forEach((file, index) => {
          fd.append(`datasetFile`, file, file.name);
        });
        // optional cover photo
        if (formData.coverPhoto)
          fd.append(
            "coverPhoto",
            formData.coverPhoto,
            formData.coverPhoto.name,
          );

        const response = await createDatasetMutation.mutateAsync(fd);
        console.log("Create dataset response:", response);
        // Pass the created dataset back to onSuccess callback
        // API returns response.data.dataset (singular)
        const newDataset = response?.data?.dataset;
        onSuccess(newDataset);
        onClose();
      }
    } catch (error: any) {
      console.log("Error in create/update datasettt:", error);
      // console.error("Failed to create/update dataset:", error);

      // Enhanced error handling for backend response structure
      // Enhanced error handling for backend response structure {data: {isSuccess: false, message: "..."}}
      let errorMessage = `Failed to ${isEdit ? "update" : "create"} dataset. Please try again.`;

      if (error && typeof error === "object") {
        // Backend API error: response.data.data.message (e.g. 409 conflict)
        if (error.response?.data?.data?.message) {
          errorMessage = error.response.data.data.message;
        }
        // Fallback response.data.message
        else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        // Direct error.data.message (existing)
        else if (error.data?.message) {
          errorMessage = error.data.message;
        }
        // Generic error strings
        else if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      toast.error(errorMessage);
      console.error("Dataset error details:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden", "body-no-scroll");
    } else {
      document.body.classList.remove("overflow-hidden", "body-no-scroll");
    }

    return () => {
      document.body.classList.remove("overflow-hidden", "body-no-scroll");
    };
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent className="max-w-[80vw] min-w-[60vw] max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            {isEdit ? "Edit Dataset" : "Add New Dataset"}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {isEdit
              ? "Update your dataset details and files"
              : "Create a new dataset with files, tags, and visibility settings"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 bg-white">
          <div className="space-y-8 max-w-4xl mx-auto">
            {/* 1. Cover Photo - Full Width */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2 text-gray-700">
                Cover Photo
              </Label>
              <div className="group relative w-full">
                <div className="w-full h-48 sm:h-64 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 group-hover:border-blue-400 transition-all duration-300">
                  {formData.coverPhoto ? (
                    <img
                      src={URL.createObjectURL(formData.coverPhoto)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : existingCoverPhotoUrl && !removeExistingCoverPhoto ? (
                    <img
                      src={existingCoverPhotoUrl}
                      alt="Current cover"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center text-gray-500">
                      <Image className="h-12 w-12 mx-auto mb-3 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      <p className="text-sm font-medium text-gray-600">Click or drag to add cover photo</p>
                      <p className="text-xs text-gray-400 mt-1">Recommended size: 1200x400 pixels (Wide)</p>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={coverPhotoInputRef}
                    onChange={(e) => handleCoverPhotoSelect(e.target.files)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept="image/*"
                  />
                  {existingCoverPhotoUrl && !removeExistingCoverPhoto && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExistingCoverPhotoUrl(null);
                        setRemoveExistingCoverPhoto(true);
                      }}
                      className="absolute top-4 right-4 bg-white/90 hover:bg-white p-2 rounded-full shadow-md border border-gray-200 transition-all opacity-0 group-hover:opacity-100 hover:scale-105 flex items-center justify-center"
                      title="Remove cover photo"
                    >
                      <X className="h-4 w-4 text-red-500 hover:text-red-600" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 2. Dataset Name */}
            <div className="space-y-3">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                Dataset Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                type="text"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="e.g. Global Tech Stock Prices 2023"
                className="w-full text-lg px-4 py-6 rounded-xl border-gray-300 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            {/* 3. Description */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">Description</Label>
              <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all shadow-sm min-h-[120px]">
                <TiptapEditor
                  value={formData.description || ""}
                  onChange={(content) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: content,
                    }))
                  }
                />
                {!formData.description && (
                  <div className="absolute pointer-events-none p-4 text-gray-400 text-sm mt-[-110px] ml-[4px]">
                    e.g. This dataset contains daily stock prices for top 50 global tech companies. It includes open, close, high, low, and volume data...
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 4. Visibility */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Visibility Status</Label>
                <RadioGroup
                  value={formData.visibility}
                  onValueChange={(value: "PRIVATE" | "PUBLIC" | "PROJECT") =>
                    handleVisibilityChange(value)
                  }
                  className="grid grid-cols-2 gap-4 h-[120px]"
                >
                  <Label
                    htmlFor="private"
                    className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all h-full ${
                      formData.visibility === "PRIVATE"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    <RadioGroupItem value="PRIVATE" id="private" className="sr-only" />
                    <Lock className={`h-6 w-6 mb-2 ${formData.visibility === "PRIVATE" ? "text-blue-500" : "text-gray-400"}`} />
                    <span className="font-semibold">Private</span>
                    <span className="text-xs text-center mt-1 opacity-80">Only you can access</span>
                  </Label>
                  
                  <Label
                    htmlFor="public"
                    className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all h-full ${
                      formData.visibility === "PUBLIC"
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    <RadioGroupItem value="PUBLIC" id="public" className="sr-only" />
                    <Eye className={`h-6 w-6 mb-2 ${formData.visibility === "PUBLIC" ? "text-green-500" : "text-gray-400"}`} />
                    <span className="font-semibold">Public</span>
                    <span className="text-xs text-center mt-1 opacity-80">Visible to everyone</span>
                  </Label>
                </RadioGroup>
              </div>

              {/* 6. Tags */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  Dataset Tags
                </Label>
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 shadow-inner h-[120px] flex flex-col">
                  <div className="flex flex-wrap gap-2 mb-3 flex-1 overflow-y-auto">
                    {formData.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-700 shadow-sm"
                      >
                        {tag}
                        <button
                          onClick={(e) => { e.preventDefault(); handleRemoveTag(tag); }}
                          className="ml-1 text-gray-400 hover:text-red-500 transition-colors bg-gray-100 hover:bg-red-50 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {formData.tags.length === 0 && (
                      <span className="text-xs text-gray-400 italic flex items-center h-full">No tags added yet</span>
                    )}
                  </div>
                  <div className="relative mt-auto">
                    <Input
                      value={tagInput}
                      onChange={handleTagInputChange}
                      onKeyDown={handleAddTag}
                      placeholder="Type tag & press Enter..."
                      className="w-full bg-white border-gray-300 text-sm pl-3 pr-10 py-2 rounded-lg"
                    />
                    <Plus className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Files Upload */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-700">
                  Data Files <span className="text-red-500">*</span>
                </Label>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                  Supported: XLS, XLSX, CSV
                </span>
              </div>
              
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-2xl transition-all cursor-pointer min-h-[180px] flex flex-col items-center justify-center p-8 gap-4 ${
                  isDragActive
                    ? "border-blue-500 bg-blue-50/50 ring-4 ring-blue-500/10"
                    : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/30 bg-gray-50/50"
                }`}
              >
                <input {...getInputProps()} />
                <div className={`p-4 rounded-full ${isDragActive ? 'bg-blue-100 text-blue-600' : 'bg-white shadow-sm text-gray-400 border border-gray-100'}`}>
                  <Upload className={`h-8 w-8 transition-colors ${isDragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                <div className="text-center space-y-1">
                  <p className="text-lg font-semibold text-gray-700">
                    {isDragActive ? "Drop your files here!" : "Drag & drop files to upload"}
                  </p>
                  <p className="text-sm text-gray-500">
                    or click to browse from your computer
                  </p>
                </div>
              </div>

              {/* Uploaded Files List */}
              {(formData?.files.length > 0 || (isEdit && existingFiles.length > 0)) && (
                <div className="mt-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    Attached Files ({(formData.files?.length || 0) + (existingFiles?.length || 0)})
                  </h4>

                  <ul className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                    {/* New Files */}
                    {formData?.files.map((file, index) => (
                      <li
                        key={`new-${index}`}
                        className="flex items-center justify-between p-3 bg-white border border-green-200 shadow-sm rounded-lg group hover:border-green-300 transition-colors"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="p-2 bg-green-50 rounded-md mr-3">
                            <FileText className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-green-600 mt-0.5">Ready to upload</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveFile(index); }}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 ml-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                    
                    {/* Existing Files */}
                    {isEdit && existingFiles.map((file: any, index: number) => (
                      <li
                        key={`existing-${index}`}
                        className="flex items-center justify-between p-3 bg-white border border-gray-200 shadow-sm rounded-lg group hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="p-2 bg-blue-50 rounded-md mr-3">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate" title={file?.originalName || file.key}>
                              {file?.originalName || file.key?.split('/').pop() || file.key}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">Existing file</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveExistingFile(index); }}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 ml-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={
              createDatasetMutation.isPending || updateDatasetMutation.isPending
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={
              !formData.title.trim() ||
              createDatasetMutation.isPending ||
              updateDatasetMutation.isPending
            }
            className="bg-bits-blue hover:bg-bits-blue/90"
          >
            {createDatasetMutation.isPending || updateDatasetMutation.isPending
              ? isEdit
                ? "Updating..."
                : "Creating..."
              : isEdit
                ? "Update Dataset"
                : "Create Dataset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewDatasetPage;
